import {
  CustomOptions,
  Data,
  get as httpGet,
  patch as httpPatch,
  post as httpPost,
  put as httpPut,
  remove as httpDelete,
} from "@toruslabs/http-helpers";

import { getAccessToken } from "@/utils/accessToken";
import { generateAuthHeaders } from "@/utils/utils";

const request = async (
  method: string,
  url: string | URL,
  queryParams: Record<string, string> = {},
  bodyData: Data = {},
  customOptions: CustomOptions = {}
) => {
  // construct url
  const searchParams = new URLSearchParams(queryParams);
  const parsedUrl = new URL(url);

  for (const pair of searchParams.entries()) {
    parsedUrl.searchParams.append(pair[0], pair[1]);
  }
  const urlWithQuery = parsedUrl.href;

  // construct options
  let options: RequestInit = {
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      ...((customOptions.headers as Headers) || {}),
    } as Headers,
  };

  if (Object.keys(bodyData).length > 0) {
    options = {
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        ...((customOptions.headers as Headers) || {}),
      } as Headers,
    };
  }

  try {
    if (method === "GET") {
      return await httpGet(urlWithQuery, options);
    }
    if (method === "POST") {
      return await httpPost(urlWithQuery, bodyData, options, { isUrlEncodedData: !!customOptions.isUrlEncodedData });
    }
    if (method === "PATCH") {
      return await httpPatch(urlWithQuery, bodyData, options, { isUrlEncodedData: !!customOptions.isUrlEncodedData });
    }
    if (method === "PUT") {
      return await httpPut(urlWithQuery, bodyData, options, { isUrlEncodedData: !!customOptions.isUrlEncodedData });
    }
    if (method === "DELETE") {
      return await httpDelete(urlWithQuery, bodyData, options, { isUrlEncodedData: !!customOptions.isUrlEncodedData });
    }
  } catch (error) {
    if ((error as Response).ok === false) {
      return (error as Response).json().then((err) => {
        throw err;
      });
    }
    throw error;
  }
  throw new Error(`Unsupported http method: ${method}`);
};

export const get = (url = "", params = {}, options = {}) => request("GET", url, params, {}, options);
export const post = (url = "", data = {}, options = {}) => request("POST", url, {}, data, options);

export const authRequest = async (method: string, url: string, queryParams: Record<string, string>, bodyData: Data, customOptions: RequestInit) => {
  const accessToken = getAccessToken();
  const headers = {
    ...customOptions.headers,
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  };

  const options = {
    ...customOptions,
    ...{ headers },
  };

  return request(method, url, queryParams, bodyData, options);
};

const signedRequest = async (method: string, url: string, queryParams: Record<string, string>, bodyData: Data, customOptions: RequestInit) => {
  const authHeaders = await generateAuthHeaders();
  const headers = {
    ...customOptions.headers,
    ...authHeaders,
  };
  const options = {
    ...customOptions,
    ...{ headers },
  };
  return request(method, url, queryParams, bodyData, options);
};

export const signedPost = async (url: string, data: Data, options: RequestInit) => signedRequest("POST", url, {}, data, options);
